import {
  createContext,
  useRef,
  useEffect,
  useCallback,
  useContext
} from 'react'
import { useChannel } from '@contexts/actionCable'

const EducatorProjectSubmissionChannelContext = createContext(null)

const EducatorProjectSubmissionChannelProvider = ({
  submissionId,
  organizationId,
  children
}) => {
  const { subscribe, unsubscribe } = useChannel()
  const listenersRef = useRef(new Set())
  const subscriptionRef = useRef(null)

  const addListener = useCallback(listener => {
    listenersRef.current.add(listener)
    return () => listenersRef.current.delete(listener)
  }, [])

  useEffect(() => {
    if (!submissionId || !organizationId) return

    // Clean up any existing subscription before creating a new one
    const cleanup = () => {
      if (subscriptionRef.current) {
        unsubscribe()
        subscriptionRef.current = null
      }
    }

    cleanup()

    subscriptionRef.current = subscribe(
      {
        channel: 'EducatorProjectSubmissionChannel',
        educator_project_submission_id: submissionId,
        organization_id: organizationId
      },
      {
        received: raw => {
          const payload = raw && raw.message ? raw.message : raw
          // Notify all registered listeners
          listenersRef.current.forEach(listener => {
            try {
              listener(payload)
            } catch (error) {
              console.error('Error in channel listener:', error)
            }
          })
        },
        disconnected: () => {
          console.warn('EducatorProjectSubmissionChannel disconnected')
        },
        rejected: () => {
          console.error('EducatorProjectSubmissionChannel subscription rejected')
        }
      }
    )

    return cleanup
  }, [submissionId, organizationId])

  return (
    <EducatorProjectSubmissionChannelContext.Provider value={{ addListener }}>
      {children}
    </EducatorProjectSubmissionChannelContext.Provider>
  )
}

const useEducatorProjectSubmissionChannel = () => {
  const ctx = useContext(EducatorProjectSubmissionChannelContext)
  if (!ctx) {
    throw new Error(
      'useEducatorProjectSubmissionChannel must be used inside EducatorProjectSubmissionChannelProvider'
    )
  }
  return ctx
}

const useEducatorProjectSubmissionChannelListener = handler => {
  const { addListener } = useEducatorProjectSubmissionChannel()

  useEffect(() => {
    const removeListener = addListener(handler)

    return removeListener
  }, [addListener])
}

export {
  EducatorProjectSubmissionChannelProvider,
  useEducatorProjectSubmissionChannel,
  useEducatorProjectSubmissionChannelListener
}
